'use client';
import ArrowIcon from '@/images/icon/arrow-default.svg';
import styles from './index.module.scss';

import { getBuyTogetherFloorData } from '@/apis/dp/service';
import CloseSvg from '@/assets/icons/dp-drawer-close.svg';
import ChangeSvg from '@/assets/icons/dp-group-buy-change-btn.svg';
import { dataExpTagConfig } from '@/components/product/const';
import UKToast from '@/components/UKToast';
import { useTranslation } from '@/i18n/client';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25217';
import { Drawer } from '@mui/material';
import { useParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import BuyProductList from './BuyProductList';
import { type CardItemProps } from './type';
interface Props {
  localInfo: any;
  shopId: string;
}
export default function BuyTogether({ localInfo, shopId }: Props) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const { sku: skuId } = useParams<Record<string, string>>();
  const [floorData, setFloorData] = useState<null | CardItemProps>(null);
  const [drawerData, setDrawerData] = useState<null | CardItemProps>(null);
  const [pageNum, setPageNum] = useState<number>(1);
  const { t } = useTranslation();
  const commonTrackParams = {
    shopid: shopId || '',
    mainskuid: skuId,
  };
  const refreshReportParams = useMemo(() => {
    return {
      ...commonTrackParams,
      ...(drawerData || floorData)?.optionList?.reduce(
        (prev: any, next, currentIndex) => {
          return {
            skuid: [
              ...prev.skuid,
              next.content.filter((item) => !item.isMainSku).map((item) => item.skuId),
            ],
            totalAmount: [...prev.totalAmount, next.totalPrice],
            sort: [...prev.sort, currentIndex + 1],
          };
        },
        {
          skuid: [],
          totalAmount: [],
          sort: [],
        },
      ),
    };
  }, [skuId, shopId, floorData, drawerData]);

  const init = async () => {
    const res = await getBuyTogetherFloorData({ skuId, pageNum });
    if (res.code === '200' && res?.data) {
      setFloorData(res?.data);
      setPageNum(pageNum + 1);
    }
  };
  const handleDrawerClose = () => {
    setOpenDrawer(false);
    // reset drawer data
    setPageNum(2);
    setDrawerData(null);
  };
  useEffect(() => {
    init();
  }, []);
  // 换一批
  const onDrawerFresh = async () => {
    const res = await getBuyTogetherFloorData({ skuId, pageNum });
    if (res?.code === '200' && res?.data) {
      setDrawerData(res.data);
      if (!res?.data?.isLastPage) {
        setPageNum(pageNum + 1);
      } else {
        UKToast.show({
          content: res?.data?.lastPageText || t('common.message.tryAgain'), // 弹窗内容
          icon: 'info', // 可选：success, fail, loading, warn, info，影响图标
        });
      }
    } else {
      UKToast.show({
        content: res?.msg || t('common.message.tryAgain'), // 弹窗内容
        icon: 'error', // 可选：success, fail, loading, warn, info，影响图标
      });
    }
    sendClickTrack.Productdetail_FreqBought_Window_Refresh_Click({
      ...refreshReportParams,
    });
  };

  return (
    <>
      {floorData && (
        <div
          className={styles.buyTogetherWrapper}
          exptag={'exp|' + EXPOSURE_RECORD.Productdetail_FreqBought_Expo}
          data-exptag-config={dataExpTagConfig}
          data-exptag-json-param={JSON.stringify(commonTrackParams)}
        >
          <h2 className={styles.header}>
            <span className={styles.title}>{floorData?.titleText}</span>
            <span
              className={styles.moreOpts}
              onClick={() => {
                sendClickTrack?.Productdetail_FreqBought_More_Click(commonTrackParams);
                setOpenDrawer(true);
              }}
            >
              {floorData?.moreText}
              <ArrowIcon className={styles.arrow} />
            </span>
          </h2>
          {floorData?.optionList && (
            <BuyProductList
              commonTrackParams={{
                sort: 1,
                ...commonTrackParams,
              }}
              data={{
                content: floorData?.optionList?.[0]?.content || [],
                totalPrice: floorData?.optionList?.[0]?.totalPrice,
                index: 0,
                i18n: {
                  buttonText: floorData.buttonText,
                  totalPriceText: floorData.totalPriceText,
                },
              }}
              localInfo={localInfo}
            />
          )}
        </div>
      )}
      <Drawer
        anchor='right'
        open={openDrawer}
        onClose={handleDrawerClose}
        // sx={{
        //   '& .MuiDrawer-paper': {
        //     width: '616px',
        //   },
        // }}
      >
        <div
          className={styles.buyTogetherDrawer}
          exptag={'exp|' + EXPOSURE_RECORD.Productdetail_FreqBought_Window_Expo}
          data-exptag-config={dataExpTagConfig}
          data-exptag-json-param={JSON.stringify(commonTrackParams)}
        >
          <header>
            <span className={styles.title}>{floorData?.titleText}</span>
            <span className={styles.close} onClick={handleDrawerClose}>
              <CloseSvg />
            </span>
          </header>
          <section className={styles.content}>
            {(drawerData || floorData)?.optionList?.map((item, index) => {
              return (
                <div className={styles.optionGroup} key={`option-list-${index}`}>
                  <h3 className={styles.optionTitle}>{`${floorData?.itemTitle} ${index + 1}`}</h3>
                  <BuyProductList
                    commonTrackParams={{
                      ...commonTrackParams,
                      sort: index + 1,
                    }}
                    data={{
                      content: item?.content || [],
                      totalPrice: item?.totalPrice,
                      index,
                      i18n: {
                        buttonText: (drawerData || floorData)?.buttonText || '',
                        totalPriceText: (drawerData || floorData)?.totalPriceText || '',
                      },
                    }}
                    localInfo={localInfo}
                    onHandleAddCart={() => {
                      handleDrawerClose();
                    }}
                  />
                </div>
              );
            })}
            <div
              className={styles.bottomChangeBar}
              exptag={'exp|' + EXPOSURE_RECORD.Productdetail_FreqBought_Window_Refresh_Expo}
              data-exptag-config={dataExpTagConfig}
              data-exptag-json-param={JSON.stringify(refreshReportParams)}
            >
              <p className={styles.textWrapper} onClick={onDrawerFresh}>
                <ChangeSvg />
                <span>{floorData?.refreshText}</span>
                {/* {`Not interested? Change`} */}
              </p>
            </div>
          </section>
        </div>
      </Drawer>
    </>
  );
}
